<script setup lang="ts">
import { ref, Ref, toRefs, computed, provide, watch, onBeforeMount } from "vue";
import { useI18n } from "vue-i18n";
import { useBaseStore } from "@/stores/base";
import SegmentDataSource from "@/modules/segments/components/SegmentDataSource.vue";
import DimensionDO = App.Data.DimensionDO;
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import SaveSegmentDialog from "@/modules/segments/components/SaveSegmentDialog.vue";
import { useApiClient } from "@/api/client";
import { useSegmentBuilderStore } from "@/modules/segments/store";
const segmentBuilderState = useSegmentBuilderStore().segmentBuilderState;
const getDataSourceForFilter = useSegmentBuilderStore().getDataSourceForFilter;
import SegmentConfigurationTopSheet from "@/modules/segments/components/SegmentConfigurationTopSheet.vue";
import DimensionFilterSelection from "@/modules/segments/components/DimensionFilterSelection.vue";
import MetricFilterSelection from "@/modules/segments/components/MetricFilterSelection.vue";
import router from "@/router/router";
import { toastApiResult } from "@/toaster";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import SegmentInsights from "@/modules/segments/components/SegmentInsights.vue";
import { useRoute, onBeforeRouteLeave } from "vue-router";
import Switch from "@/ui/components/switch/switch.vue";
import { unsavedChangesDialog } from "@/ui/composables/useUnsavedChangesDialog";
const { t } = useI18n();

const props = defineProps<{
	allAvailableDimensions: DimensionDO[];
	isUnsavedSegmentFromExplorer: boolean;
}>();

const { allAvailableDimensions } = toRefs(props);

const showSaveSegmentDialog: Ref<boolean> = ref(false);
const userSelectedExpertMode: Ref<boolean> = ref(false);

const filterToEdit: Ref<ActiveFilterDTO> = ref();
const isDimensionFilterOpen = ref(false);
const filterPartType: Ref<"include" | "exclude"> = ref("include");
const filterDataSource: Ref<string> = ref("");

const computedBreadcrumbs = computed(() => {
	return [
		{
			title: "Segments",
			disabled: false,
			to: "/segments",
		},
		{
			title: segmentBuilderState.segment.title,
			disabled: true,
			to: "/segments",
		},
	];
});

const introspectSegment = async () => {
	segmentBuilderState.introspectResult = [];
	const response = await useApiClient("segments").introspect(
		segmentBuilderState.segment
	);
	if (response.isOk()) {
		segmentBuilderState.introspectResult = response.value;
		segmentBuilderState.circularSegmentDependencyDetected = false;
	}
	if (response.isErr()) {
		segmentBuilderState.introspectErrorDetected = true;
		if (response.error.status === 422) {
			segmentBuilderState.circularSegmentDependencyDetected = true;
		}
	}
};

watch(
	() => segmentBuilderState.segment.parts,
	() => {
		introspectSegment();
	},
	{ deep: true }
);

onBeforeMount(() => {
	introspectSegment();
	window.addEventListener("beforeunload", beforeUnload);
});

const resource_type: string | null =
	useRoute().params.resource_type !== ""
		? String(useRoute().params.resource_type)
		: null;

const resource_uuid: string | null =
	useRoute().params.resource_uuid !== ""
		? String(useRoute().params.resource_uuid)
		: null;

const saveSegment = async () => {
	if (!segmentBuilderState.segment.id) {
		// open save-as modal for new Segment from Document
		showSaveSegmentDialog.value = true;
	} else {
		// Update existing Segment
		await updateSegment();
		router.push({
			name: "segments.configuration",
			params: {
				resource_type: resource_type,
				resource_uuid: resource_uuid,
			},
		});

		activeTab.value = "segment_builder";
	}
};

const updateSegment = async () => {
	const result = await toastApiResult(
		useApiClient("segments").update(segmentBuilderState.segment)
	);
	if (result.isOk()) {
		useSegmentBuilderStore().segmentBuilderState.segment.has_dependency_on_lost_customer_segment =
			result.value.has_dependency_on_lost_customer_segment;
		setTimeout(() => {
			useSegmentBuilderStore().segmentBuilderState.segmentHasChanges = false;
		}, 100);
	}
};

const isExpertMode = computed(
	(): boolean =>
		userSelectedExpertMode.value ||
		segmentBuilderState.segment.parts.some((part) => part.type === "exclude")
);

const expertModeCantBeToggled = computed(() => {
	return !(
		segmentBuilderState.segment.parts.filter((part) => part.type === "exclude")
			.length === 0
	);
});

const showDataSourceGrouping = computed(() => {
	const uniqueTypesCount = new Set(
		segmentBuilderState.segment.parts.map((part) => part.data_source)
	).size;
	return uniqueTypesCount > 1 || isExpertMode.value;
});

const showFilterBar = (dataSource: string, partType: "include" | "exclude") => {
	filterDataSource.value = dataSource;
	filterPartType.value = partType;

	// Metric filters still live in the secondary drawer; dimension filters moved
	// to the filter dialog.
	if (dataSource === "metric") {
		useBaseStore().showSecondaryDrawer = true;
		return;
	}

	isDimensionFilterOpen.value = true;
};
const hideFilterBar = () => {
	filterDataSource.value = "";
	filterPartType.value = "include";
	filterToEdit.value = null;
	isDimensionFilterOpen.value = false;
	useBaseStore().showSecondaryDrawer = false;
};

const editFilter = (
	activeFilter: ActiveFilterDTO,
	partType: "include" | "exclude"
) => {
	filterDataSource.value = getDataSourceForFilter(activeFilter);
	filterToEdit.value = activeFilter;
	showFilterBar(filterDataSource.value, partType);
};
provide("editFilter", editFilter);

watch(
	() => useBaseStore().showSecondaryDrawer,
	(newValue) => {
		if (!newValue) {
			// 'hack' to reset defaults again, because drawer is not persistent
			hideFilterBar();
		}
	}
);

const newSegmentSuccessCallback = () => {
	setTimeout(() => {
		router.push("/segments");
	}, 300);
};

const closeSegmentBuilder = () => {
	router.push("/segments");
};

const activeTab = ref<string>("segment_builder");
const tabs = ref<object>([
	{
		name_clean: "segment_builder",
		icon: "fa: fa-light fa-sliders",
		disabled: false,
	},
	{
		name_clean: "segment_insights",
		icon: "fa: fa-light fa-lightbulb-on",
		disabled: props.isUnsavedSegmentFromExplorer,
	},
]);

onBeforeRouteLeave((to, from, next) => {
	const leavingBuilderModule = !to.name
		?.toString()
		.startsWith("segments.configuration");
	unsavedChangesDialog(
		"segment",
		leavingBuilderModule &&
			useSegmentBuilderStore().segmentBuilderState.segmentHasChanges,
		next,
		() => {
			useSegmentBuilderStore().resetState();
		}
	);
});

const beforeUnload = (event: BeforeUnloadEvent) => {
	if (useSegmentBuilderStore().segmentBuilderState.segmentHasChanges) {
		event.preventDefault();
		event.returnValue = true;
	}
};
</script>

<template>
	<save-segment-dialog
		:segment-to-save="segmentBuilderState.segment"
		:on-success="newSegmentSuccessCallback"
		v-model="showSaveSegmentDialog"
	/>

	<teleport to="#secondary-drawer">
		<metric-filter-selection
			v-if="filterDataSource === 'metric' && useBaseStore().showSecondaryDrawer"
			:filter-part-type="filterPartType"
			@close-filter-bar="hideFilterBar"
		/>
	</teleport>

	<dimension-filter-selection
		v-model="isDimensionFilterOpen"
		:filter-data-source="filterDataSource"
		:filter-to-edit="filterToEdit"
		:all-available-dimensions="allAvailableDimensions"
		:filter-part-type="filterPartType"
		:is-expert-mode="isExpertMode"
		@close-filter-bar="hideFilterBar"
	/>

	<main-breadcrumbs :breadcrumbs="computedBreadcrumbs" />

	<v-card elevation="2">
		<segment-configuration-top-sheet />
		<v-divider />
		<div>
			<v-tabs v-model="activeTab" align-tabs="start">
				<v-tab
					v-for="tab in tabs"
					:key="tab.name_clean"
					:text="t(`segments.tabs.${tab.name_clean}`)"
					:value="tab.name_clean"
					:prepend-icon="tab.icon"
					:disabled="tab.disabled"
				/>
			</v-tabs>
		</div>
		<v-divider />
		<v-alert
			class="ma-5"
			icon="fa: fa-light fa-circle-exclamation"
			prominent
			v-if="segmentBuilderState.circularSegmentDependencyDetected"
			color="error"
			variant="tonal"
			border="start"
		>
			This segment configuration cannot be processed because it creates a
			circular dependency. One of the filters refers back to this segment,
			directly or indirectly. Please adjust your filters to remove this
			unprocessable loop.
		</v-alert>
		<div class="px-5">
			<v-window v-model="activeTab" class="flex-grow-1">
				<v-window-item key="segment_builder" value="segment_builder">
					<div v-if="activeTab === 'segment_builder'">
						<div class="d-flex pt-5">
							<v-spacer />
							<Switch
								v-model="userSelectedExpertMode"
								:disabled="expertModeCantBeToggled"
								label="Expert mode"
								hide-details
							/>
						</div>
						<segment-data-source
							v-for="(supportedDataSource, index) in useSegmentBuilderStore()
								.segmentBuilderState.supportedDataSourcesInSegmentBuilder"
							:key="index"
							:data-source="supportedDataSource"
							:show-data-source-grouping="showDataSourceGrouping"
							:is-expert-mode="isExpertMode"
							@add-dimension-filter="(partType:'include'|'exclude') => showFilterBar(supportedDataSource, partType)"
							@add-metric-filter="(partType:'include'|'exclude') => showFilterBar('metric', partType)"
						/>
					</div>
				</v-window-item>
				<v-window-item
					key="segment_insights"
					value="segment_insights"
					class="px-1"
				>
					<div class="py-5">
						<segment-insights v-if="activeTab === 'segment_insights'" />
					</div>
				</v-window-item>
			</v-window>
		</div>
	</v-card>
	<div class="pt-10 d-flex">
		<v-spacer />
		<v-btn
			color="error"
			variant="text"
			class="mr-5"
			@click="closeSegmentBuilder()"
		>
			Close
		</v-btn>
		<v-badge
			:model-value="
				useSegmentBuilderStore().segmentBuilderState.segmentHasChanges
			"
			color="error"
			offset-x="4"
			offset-y="4"
			dot
		>
			<v-btn
				@click="saveSegment()"
				:disabled="segmentBuilderState.circularSegmentDependencyDetected"
			>
				Save Segment
			</v-btn>
		</v-badge>
	</div>
</template>

<style scoped lang="scss">
.color-grey {
	color: rgba(0, 0, 0, 0.5);
}
.data-source-group {
	border: 1px dashed #f5f5f5;
	border-radius: 3px;
	position: relative;

	.label {
		position: absolute;
		color: rgba(0, 0, 0, 0.5);
		background-color: #f5f5f5;
		top: -28px;
		left: 0;
	}
}
</style>
